import { useMemo } from 'react';
import { Space, Tag, Typography } from 'antd';
import { DataTable, EM_DASH, type DataColumn } from '@viq-admin/ui';
import type { DeviceInstall } from '../../../api/telemetry';
import { UserLink } from '../../../components/EntityLinks';
import { compareVersions } from '../../../views/Telemetry/model';
import { sortInstalls } from './model';
import { useCompanyInstalls } from './useCompanyHubData';

/**
 * This company's app installs (one row per install, from mobile telemetry):
 * who, which phone and app version, when it was last seen and last synced,
 * and whether leads are stuck on it. Stranded installs sort first, since
 * they are the ones that need a call to the rep. The full event log for an
 * install stays on the Telemetry page.
 *
 * Uncontrolled table (see CompanyUsersTab).
 */

const PLATFORM_LABEL: Record<string, string> = { ios: 'iOS', android: 'Android' };

function platformText(i: DeviceInstall): string {
  if (!i.platform) return EM_DASH;
  const os = i.osVersion && i.osVersion !== 'unknown' ? ` ${i.osVersion}` : '';
  return `${PLATFORM_LABEL[i.platform] ?? i.platform}${os}`;
}

const userText = (i: DeviceInstall) => i.userName || i.userEmail || '';

export function CompanyDevicesTab({ companyId }: { companyId: string }) {
  const { data, isLoading, error, refetch } = useCompanyInstalls(companyId);
  const installs = data?.installs;
  const rows = useMemo(() => sortInstalls(installs ?? []), [installs]);

  const columns: DataColumn<DeviceInstall>[] = [
    {
      key: 'stranded',
      dataIndex: 'stranded',
      title: 'Status',
      kind: 'enum',
      width: 110,
      projectValue: (r) => (r.stranded ? 'Stranded' : 'OK'),
      render: (_: unknown, r: DeviceInstall) =>
        r.stranded ? (
          <Tag color="error" title="Unsynced rows on the device and no successful sync in 24 hours">
            Stranded
          </Tag>
        ) : (
          <Typography.Text type="secondary">{EM_DASH}</Typography.Text>
        ),
    },
    {
      key: 'user',
      dataIndex: 'userName',
      title: 'User',
      kind: 'text',
      width: 220,
      projectValue: userText,
      render: (_: unknown, r: DeviceInstall) => (
        <Space direction="vertical" size={0}>
          <UserLink oid={r.oid} name={r.userName || r.userEmail} />
          {r.userName && r.userEmail && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {r.userEmail}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      key: 'platform',
      dataIndex: 'platform',
      title: 'Platform',
      kind: 'text',
      width: 140,
      projectValue: platformText,
      render: (_: unknown, r: DeviceInstall) => platformText(r),
    },
    {
      key: 'appVersion',
      dataIndex: 'appVersion',
      title: 'App version',
      kind: 'text',
      width: 170,
      sortable: (a, b) => compareVersions(a.appVersion, b.appVersion),
      render: (_: unknown, r: DeviceInstall) =>
        r.appVersion ? (
          <Space size={4}>
            <span>
              {r.appVersion}
              {r.build ? ` (${r.build})` : ''}
            </span>
            {r.outdated && (
              <Tag color="warning" title="Older than the newest version in the fleet">
                Outdated
              </Tag>
            )}
          </Space>
        ) : (
          EM_DASH
        ),
    },
    { key: 'lastSeenAt', dataIndex: 'lastSeenAt', title: 'Last seen', kind: 'dateTime', width: 180 },
    { key: 'lastSyncOkAt', dataIndex: 'lastSyncOkAt', title: 'Last synced', kind: 'dateTime', width: 180 },
    {
      key: 'unsyncedCount',
      dataIndex: 'unsyncedCount',
      title: 'Unsynced',
      kind: 'number',
      width: 110,
      render: (v: number | null) => (v === null ? EM_DASH : v),
    },
  ];

  return (
    <DataTable<DeviceInstall>
      dataSource={rows}
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
      columns={columns}
      rowKey="installId"
      storageKey="companyHubDevices"
      searchFields={['userName', 'userEmail', 'appVersion']}
      searchPlaceholder="Search user or version"
      empty={{
        title: 'No installs yet',
        description: "An install appears once a build that sends telemetry signs in to this company.",
      }}
    />
  );
}
